import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "@/components/HeroCarousel";
import ProductCard from "@/components/ProductCard";
import { api, CATEGORIES } from "@/lib/api";
import { ArrowRight, Leaf, Truck, Heart, ShieldCheck } from "lucide-react";

const CATEGORY_HERO = {
  bedding: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/5d96ae127f122e340202493ff75cd939ea9d1aaf56d7d13eb16d26279e7b7519.png",
  blankets: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/441cb965fc168cde911cb24ee4aade2aa209f656e8042730963495897acc41fb.png",
  "cushion-covers": "https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?auto=format&fit=crop&w=1200&q=80",
  curtains: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/9789fdc267bb5924073b16d729ad3de095efc77b79ce1c2eff9b0c6d2308858c.png",
};

const CATEGORY_BLURB = {
  bedding: "Premium · Export · Cotton Satin · Kids Bedding",
  blankets: "Hand-finished, season-spanning warmth",
  "cushion-covers": "Cushion covers, plush towels & soft pillows",
  curtains: "Sheer & velvet, standard 7.5 × 4.5 ft",
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/products?featured=true&limit=8").then((r) => setFeatured(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <HeroCarousel />

      {/* Brand intro */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-24 lg:py-28 text-center">
        <div className="label-eyebrow !text-[10px] sm:!text-xs mb-3 sm:mb-4">Soft Luxury · Nature</div>
        <h2 className="font-display text-[2rem] leading-[1.1] sm:text-5xl lg:text-6xl text-ink sm:leading-[1.05] tracking-tight max-w-3xl mx-auto">
          The bedroom, reimagined as a quiet sanctuary.
        </h2>
        <p className="mt-4 sm:mt-6 text-ink-soft text-sm sm:text-lg max-w-xl mx-auto leading-relaxed">
          Each Bedding Lounge piece is selected for the way it feels at 3 AM — the hour that tells the truth about a fabric.
        </p>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-14 sm:pb-20" data-testid="home-categories">
        <div className="flex items-end justify-between mb-6 sm:mb-12">
          <div>
            <div className="label-eyebrow !text-[10px] sm:!text-xs mb-1.5 sm:mb-2">Shop by Category</div>
            <h3 className="font-display text-2xl sm:text-4xl text-ink">Four collections, one feeling.</h3>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group relative aspect-[3/2] sm:aspect-[16/10] overflow-hidden bg-cream-warm"
              data-testid={`home-category-${c.slug}`}
            >
              <img
                src={CATEGORY_HERO[c.slug]}
                alt={c.label}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-transparent sm:from-ink/70 sm:via-ink/20" />
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 text-cream">
                <div className="font-display text-2xl sm:text-4xl tracking-tight">{c.label}</div>
                <div className="text-cream/85 text-xs sm:text-sm mt-1">{CATEGORY_BLURB[c.slug]}</div>
                <div className="mt-3 sm:mt-4 flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold">
                  Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-14 sm:pb-20" data-testid="home-featured">
          <div className="mb-6 sm:mb-10">
            <div className="label-eyebrow !text-[10px] sm:!text-xs mb-1.5 sm:mb-2">New Arrivals</div>
            <h3 className="font-display text-2xl sm:text-4xl text-ink">Hand-picked for the season.</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-8 sm:gap-x-6 sm:gap-y-12">
            {featured.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* Promise */}
      <section className="bg-cream-warm border-y border-border-subtle">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-12">
            {[
              { icon: Leaf, title: "Natural Fibers", text: "Cotton, linen, soft wovens" },
              { icon: Truck, title: "Cash on Delivery", text: "Pakistan-wide shipping" },
              { icon: Heart, title: "Hand Finished", text: "Crafted with care" },
              { icon: ShieldCheck, title: "Easy Returns", text: "7-day exchange policy" },
            ].map((f) => (
              <div key={f.title} className="text-center">
                <f.icon size={24} className="text-sage mx-auto mb-2 sm:mb-3" />
                <div className="font-display text-lg sm:text-xl text-ink">{f.title}</div>
                <div className="text-xs sm:text-sm text-ink-soft mt-1">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
